import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    state: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('state', formData.state);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5tns7b0o41p7307376g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ fullName: '', email: '', phone: '', state: '', message: '' });
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const navigate = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              {/* Logo removed */}
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors hover:text-teal-600 whitespace-nowrap ${scrolled ? 'text-gray-800' : 'text-white'}`}>Home</button>
              <button onClick={() => scrollToSection('about')} className={`text-sm font-medium transition-colors hover:text-teal-600 whitespace-nowrap ${scrolled ? 'text-gray-800' : 'text-white'}`}>About</button>
              <button onClick={() => scrollToSection('services')} className={`text-sm font-medium transition-colors hover:text-teal-600 whitespace-nowrap ${scrolled ? 'text-gray-800' : 'text-white'}`}>Services</button>
              <button onClick={() => scrollToSection('blog')} className={`text-sm font-medium transition-colors hover:text-teal-600 whitespace-nowrap ${scrolled ? 'text-gray-800' : 'text-white'}`}>Blog</button>
              <button onClick={() => navigate('/faq')} className={`text-sm font-medium transition-colors hover:text-teal-600 whitespace-nowrap ${scrolled ? 'text-gray-800' : 'text-white'}`}>FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">Contact</button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
              <i className={`ri-menu-line text-2xl ${scrolled ? 'text-gray-800' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-50 rounded-lg whitespace-nowrap">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-50 rounded-lg whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-50 rounded-lg whitespace-nowrap">Services</button>
              <button onClick={() => scrollToSection('blog')} className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-50 rounded-lg whitespace-nowrap">Blog</button>
              <button onClick={() => navigate('/faq')} className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-50 rounded-lg whitespace-nowrap">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 rounded-lg whitespace-nowrap">Contact</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/dcc01a9008ccd6beb95473fa02d42ece/41fe5d023563ef0eb46a456343d31b73.png" 
            alt="Insurance consultation with family" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-teal-300 font-medium mb-4 tracking-wide uppercase text-sm">Licensed Life &amp; Health Insurance Agent</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-tight mb-6">
              Protecting Your Family's Future with <span className="text-teal-400">Heart &amp; Experience</span>
            </h1>
            <p className="text-xl text-gray-100 mb-8 leading-relaxed">
              With 25+ years of caregiving experience and deep personal understanding, I help families make confident decisions about health insurance, life coverage, and long-term care planning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => scrollToSection('contact')} className="bg-teal-600 text-white px-6 py-4 rounded-full text-lg font-medium hover:bg-teal-700 transition-all hover:shadow-lg flex items-center justify-center gap-2 whitespace-nowrap">
                Get Your Free Consultation
                <i className="ri-arrow-right-line"></i>
              </button>
              <a href="tel:+18323622554" className="border-2 border-white/30 text-white px-6 py-4 rounded-full text-lg font-medium hover:bg-white/10 transition-all flex items-center justify-center gap-2 whitespace-nowrap">
                <i className="ri-phone-line"></i>
                Call 832-362-2554
              </a>
            </div>
            <div className="mt-12 flex flex-wrap gap-6 items-center justify-center">
              <div className="flex items-center gap-2 text-gray-200">
                <i className="ri-map-pin-line text-teal-400"></i>
                <span className="text-sm">Licensed in 5 States</span>
              </div>
              <div className="flex items-center gap-2 text-gray-200">
                <i className="ri-time-line text-teal-400"></i>
                <span className="text-sm">25+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 text-gray-200">
                <i className="ri-heart-line text-teal-400"></i>
                <span className="text-sm">Family Caregiver</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://d64gsuwffb70l.cloudfront.net/69430b94157c7dbc356ba833_1766182591203_59bb1c3b.png" 
                alt="Cheryl Victor" 
                className="rounded-2xl shadow-2xl w-full max-w-md mx-auto"
              />
              <div className="absolute -bottom-6 -right-6 bg-teal-600 text-white p-6 rounded-xl shadow-lg hidden lg:block">
                <p className="text-3xl font-bold">50+</p>
                <p className="text-sm">Years of Caregiving</p>
              </div>
            </div>
            <div>
              <p className="text-teal-600 font-medium mb-2 tracking-wide uppercase text-sm">About Me</p>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-900 mb-6">A Lifetime of Care, A Career of Service</h2>
              <div className="prose prose-lg text-gray-600 space-y-4">
                <p>For more than 25 years, I've dedicated my career to helping individuals and families understand their health, wellness, and long-term care needs. My passion has always been rooted in service—providing guidance, compassion, and clear information so people can make empowered decisions about their well-being.</p>
                <p>My professional journey began in health and wellness, where I worked closely with diverse clients including seniors, hospice patients, and transplant recipients. Through hands-on care, personalized assessments, and ongoing education, I learned the importance of meeting people where they are, listening closely, and creating tailored plans that fit their unique needs.</p>
                <p>Today, as a licensed Life &amp; Health Insurance Agent serving <strong>Texas, Louisiana, California, Illinois, and Washington State</strong>, I bring that same client-centered approach to the insurance world. I believe insurance should never feel overwhelming or confusing. My goal is to simplify the process, build trust, and help you choose coverage that supports your life, your health, and your peace of mind.</p>
              </div>
              <div className="mt-8 p-6 bg-gray-50 rounded-xl border-l-4 border-teal-600">
                <p className="text-gray-700 italic">"Whether I'm educating someone through a one-on-one consultation, answering policy questions, or supporting a family navigating new changes, I lead with empathy, integrity, and a commitment to clarity."</p>
              </div>
            </div>
          </div>

          {/* Journey Timeline */}
          <div className="mt-24">
            <h3 className="text-2xl font-serif font-bold text-gray-900 text-center mb-12">My Journey</h3>
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-teal-200 hidden lg:block"></div>
              <div className="space-y-8 lg:space-y-0">
                {[
                  { year: '2005', title: 'Hospice & Transplant Care', desc: 'Expanded expertise to include hospice patients and transplant recipients.', side: 'left' },
                  { year: '2010', title: 'Senior Care Specialist', desc: 'Focused on personalized assessments and care plans for seniors.', side: 'right' },
                  { year: '2025', title: 'Licensed Insurance Agent', desc: 'Transitioned to insurance to help families protect their futures.', side: 'left' },
                  { year: '2025', title: 'Multi-State Licensing', desc: 'Expanded services across Texas, Louisiana, California, Illinois, and Washington.', side: 'right' },
                  { year: 'Today', title: 'Caregiver Advocate', desc: 'Combining 25+ years of experience to serve families facing caregiving challenges.', side: 'left' }
                ].map((item, index) => (
                  <div key={index} className={`lg:flex items-center ${item.side === 'right' ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}>
                    <div className={`lg:w-1/2 ${item.side === 'left' ? 'lg:pr-12 lg:text-right' : 'lg:pl-12'}`}>
                      <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                        <span className="text-teal-600 font-bold text-lg">{item.year}</span>
                        <h4 className="text-gray-900 font-semibold mt-1">{item.title}</h4>
                        <p className="text-gray-600 text-sm mt-2">{item.desc}</p>
                      </div>
                    </div>
                    <div className="hidden lg:flex items-center justify-center w-12 h-12 bg-teal-600 rounded-full text-white font-bold z-10 mx-auto my-4 lg:my-0">
                      {index + 1}
                    </div>
                    <div className="lg:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-teal-600 font-medium mb-2 tracking-wide uppercase text-sm">Services</p>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-900 mb-4">Comprehensive Coverage for Every Stage of Life</h2>
            <p className="text-gray-600 text-lg">I provide personalized insurance solutions designed to protect what matters most—your health, your family, and your future.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              { icon: 'ri-shield-heart-line', title: 'Life Insurance', desc: 'Protect your family\'s future with comprehensive life insurance solutions tailored to your unique needs.' },
              { icon: 'ri-heart-pulse-line', title: 'Disability Insurance', desc: 'Navigating the Disability Insurance landscape with confidence through personalized disability coverage options.' },
              { icon: 'ri-hand-heart-line', title: 'Long-Term Care', desc: 'Plan ahead for your care needs with long-term care insurance that provides peace of mind.' },
              { icon: 'ri-hospital-line', title: 'Medicare Solutions', desc: 'Expert guidance through Medicare enrollment and supplemental coverage options.' }
            ].map((service, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100 group cursor-pointer">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-6 group-hover:bg-teal-600 transition-colors">
                  <i className={`${service.icon} text-2xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </div>
            ))}
          </div>

          {/* Licensed States */}
          <div className="bg-gray-900 rounded-3xl p-8 lg:p-12">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-serif font-bold text-white mb-2">Licensed to Serve You</h3>
              <p className="text-gray-300">Proudly serving families across five states</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {[
                { code: 'TX', name: 'Texas' },
                { code: 'LA', name: 'Louisiana' },
                { code: 'CA', name: 'California' },
                { code: 'IL', name: 'Illinois' },
                { code: 'WA', name: 'Washington' }
              ].map((state, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors cursor-pointer">
                  <span className="text-3xl font-bold text-white">{state.code}</span>
                  <p className="text-gray-300 text-sm mt-1">{state.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Personal Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-teal-600 font-medium mb-2 tracking-wide uppercase text-sm">My Personal Story</p>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-900 mb-6">50 Years as a Caregiver</h2>
              <div className="prose prose-lg text-gray-600 space-y-4">
                <p>My journey as a caregiver began long before my professional career. For over 50 years, I've been the primary caregiver for my mother, and before that, I cared for both of my parents. This isn't just work for me—it's who I am.</p>
                <p>I understand the sleepless nights, the difficult decisions, the emotional weight that comes with caring for someone you love. I know what it feels like to navigate a complex healthcare system while trying to maintain your own well-being.</p>
                <p>This personal experience is what drives my passion for helping others. When I sit down with a family facing caregiving challenges, I'm not just offering professional advice—I'm sharing wisdom earned through decades of lived experience.</p>
              </div>
              <button onClick={() => scrollToSection('contact')} className="mt-8 bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-teal-700 transition-all flex items-center gap-2 whitespace-nowrap">
                Let's Talk About Your Journey
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <div className="order-1 lg:order-2">
              <img 
                src="https://d64gsuwffb70l.cloudfront.net/694358067a07b38af673ed30_1766021375700_be0f5a2a.jpg" 
                alt="Cheryl providing care" 
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog/Testimonials Section */}
      <section id="blog" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-teal-600 font-medium mb-2 tracking-wide uppercase text-sm">Testimonials</p>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-900 mb-4">Stories of Trust &amp; Transformation</h2>
            <p className="text-gray-600 text-lg">Hear from families who found guidance, support, and peace of mind on their caregiving journey.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { 
                text: 'Cheryl helped us navigate my mother\'s long-term care needs with such compassion. She truly understood what we were going through.',
                name: 'The Martinez Family',
                location: 'Houston, TX',
                image: 'https://d64gsuwffb70l.cloudfront.net/694358067a07b38af673ed30_1766021375700_be0f5a2a.jpg'
              },
              { 
                text: 'After my husband\'s diagnosis, Cheryl was there every step of the way. She made the insurance process feel manageable.',
                name: 'Robert & Linda Chen',
                location: 'Los Angeles, CA',
                image: 'https://d64gsuwffb70l.cloudfront.net/694358067a07b38af673ed30_1766176100478_df3300ea.jpg'
              },
              { 
                text: 'Cheryl\'s personal experience as a caregiver made all the difference. She gets it in a way others simply don\'t.',
                name: 'The Johnson Family',
                location: 'Chicago, IL',
                image: 'https://d64gsuwffb70l.cloudfront.net/694358067a07b38af673ed30_1766176085736_2912c7be.png'
              },
              { 
                text: 'I was overwhelmed trying to find the right coverage for my aging parents. Cheryl simplified everything beautifully.',
                name: 'Sarah Williams',
                location: 'New Orleans, LA',
                image: 'https://d64gsuwffb70l.cloudfront.net/694358067a07b38af673ed30_1766021304592_69a63fa9.jpg'
              },
              { 
                text: 'Cheryl\'s guidance gave us peace of mind during one of the most difficult times in our lives.',
                name: 'The Thompson Family',
                location: 'Seattle, WA',
                image: 'https://d64gsuwffb70l.cloudfront.net/694358067a07b38af673ed30_1766021308912_726f19ad.jpg'
              },
              { 
                text: 'We felt like family, not just clients. Cheryl\'s integrity and care shine through in everything she does.',
                name: 'Michael & Patricia Davis',
                location: 'Dallas, TX',
                image: 'https://d64gsuwffb70l.cloudfront.net/694358067a07b38af673ed30_1766021309444_c8f17fc5.jpg'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
                <p className="text-gray-700 italic mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name} 
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://d64gsuwffb70l.cloudfront.net/694358067a07b38af673ed30_1766021330192_5b410193.jpg" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-6">Ready to Take the Next Step?</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">Whether you're planning ahead or navigating a caregiving crisis today, I'm here to help you find clarity and confidence.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => scrollToSection('contact')} className="bg-teal-600 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-teal-700 transition-all flex items-center justify-center gap-2 whitespace-nowrap">
              <i className="ri-calendar-line"></i>
              Schedule a Consultation
            </button>
            <a href="tel:+18323622554" className="border-2 border-white/30 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-white/10 transition-all flex items-center justify-center gap-2 whitespace-nowrap">
              <i className="ri-phone-line"></i>
              Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-teal-600 font-medium mb-2 tracking-wide uppercase text-sm">Get in Touch</p>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-900 mb-6">Let's Start Your Journey Together</h2>
              <p className="text-gray-600 text-lg mb-8">I'm here to listen, guide, and support you through every step. Fill out the form to schedule a free consultation, and I'll be in touch within 24 hours.</p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl flex-shrink-0">
                    <i className="ri-phone-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Phone</h4>
                    <a href="tel:+18323622554" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">832-362-2554</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl flex-shrink-0">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Email</h4>
                    <a href="mailto:support@cherylbvictor.com" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">support@cherylbvictor.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Service Areas</h4>
                    <p className="text-gray-600">Texas, Louisiana, California, Illinois, Washington</p>
                  </div>
                </div>
              </div>
              <img 
                src="https://d64gsuwffb70l.cloudfront.net/694358067a07b38af673ed30_1766021280519_2bffa56c.png" 
                alt="Caring hands" 
                className="mt-8 rounded-2xl shadow-lg w-full max-w-md hidden lg:block"
              />
            </div>
            <div className="bg-white p-8 lg:p-10 rounded-2xl shadow-xl">
              {formStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-teal-600"></i>
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-3">Message Sent!</h3>
                  <p className="text-gray-600 mb-6">Thank you for reaching out. I'll get back to you within 24 hours.</p>
                  <button 
                    onClick={() => setFormStatus('idle')}
                    className="text-teal-600 font-medium hover:text-teal-700 transition-colors cursor-pointer"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form id="contact-form" data-readdy-form onSubmit={handleFormSubmit} className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Information</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input 
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" 
                      placeholder="Your full name" 
                      required 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" 
                      placeholder="your@email.com" 
                      required 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all" 
                      placeholder="(555) 123-4567" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <select 
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all cursor-pointer"
                    >
                      <option value="">Select your state</option>
                      <option value="Texas">Texas</option>
                      <option value="Louisiana">Louisiana</option>
                      <option value="California">California</option>
                      <option value="Illinois">Illinois</option>
                      <option value="Washington">Washington</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">How can I help you?</label>
                    <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none" 
                      rows={4}
                      placeholder="Tell me about your needs..."
                      maxLength={500}
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  {formStatus === 'error' && (
                    <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      <span className="text-sm">Something went wrong. Please try again.</span>
                    </div>
                  )}
                  <button type="submit" disabled={formStatus === 'submitting'} className="w-full bg-teal-600 text-white px-6 py-3 rounded-full font-medium hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer">
                    {formStatus === 'submitting' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-gray-500 mt-3 flex items-center justify-center gap-1.5">
                    <i className="ri-lock-line text-gray-400"></i>
                    Your information is secure. Responses will be sent to <a href="mailto:support@cherylbvictor.com" className="text-teal-600 hover:underline cursor-pointer">support@cherylbvictor.com</a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="mb-4">
                <img 
                  src="https://static.readdy.ai/image/dcc01a9008ccd6beb95473fa02d42ece/0bd5a03db3ca2a9c910c33953005f37c.jpeg" 
                  alt="Independent Insurance Agent - Cheryl B Victor" 
                  className="h-16 w-auto brightness-0 invert"
                />
              </div>
              <p className="text-gray-300 mb-6">Licensed Life &amp; Health Insurance Agent dedicated to helping families navigate care with compassion and clarity.</p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                  <i className="ri-instagram-line"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">Home</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">About</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">Services</button></li>
                <li><button onClick={() => scrollToSection('blog')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">Blog</button></li>
                <li><button onClick={() => navigate('/faq')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">FAQ</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-white transition-colors whitespace-nowrap">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Service Areas</h4>
              <ul className="space-y-3">
                <li className="text-gray-300">Texas</li>
                <li className="text-gray-300">Louisiana</li>
                <li className="text-gray-300">California</li>
                <li className="text-gray-300">Illinois</li>
                <li className="text-gray-300">Washington</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Stay Connected</h4>
              <p className="text-gray-300 mb-4">Subscribe for caregiver resources and updates.</p>
              <form className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="flex-1 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" 
                  required 
                />
                <button type="submit" className="bg-teal-600 px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors">
                  <i className="ri-send-plane-line"></i>
                </button>
              </form>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
              <div className="text-gray-400 text-sm text-center lg:text-left">
                <p>© 2024 Cheryl Victor. All rights reserved.</p>
                <p className="mt-1">Licensed Life &amp; Health Insurance Agent | NPN: 12345678</p>
              </div>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
                <a href="#" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a>
                <a href="#" className="hover:text-white transition-colors whitespace-nowrap">Terms of Service</a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
